import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, GitBranch, Megaphone } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { SectionCard } from '../components/common/SectionCard';
import { EmptyState } from '../components/common/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Button } from '../components/ui/Button';
import { Separator } from '../components/ui/Separator';
import { AiInsightRail } from '../components/ai/AiInsightRail';
import { DecisionDialog, type DecisionKind } from '../components/applications/DecisionDialog';
import { StageStepper } from '../components/lifecycle/StageStepper';
import { GatePanel } from '../components/lifecycle/GatePanel';
import { DocumentStatusBadge, RecordStatusPill, TrackPill } from '../components/common/StatusPills';
import { ScoreChip } from '../components/common/ScoreChip';
import { usePlatform } from '../contexts/PlatformContext';
import { entityById, entityName } from '../data/entities';
import { criteriaSets } from '../data/criteria';
import { formatAed, formatDate, daysUntil } from '../utils/format';
import { STAGE_META, trackLabel } from '../utils/stages';
export function ApplicationReview() {
  const {
    id = ''
  } = useParams();
  const {
    applicationById
  } = usePlatform();
  const record = applicationById(id);
  const [decision, setDecision] = React.useState<DecisionKind | null>(null);
  if (!record) {
    return <div className="p-6" data-id="element-854" data-mp="sy508k4eqzbu">
        <EmptyState title="Record not found" description="This application reference does not exist in the current environment." action={<Link to="/applications" className="inline-flex items-center rounded-sm bg-uae-green px-3 py-2 text-[13px] font-medium text-white" data-id="element-856" data-mp="k5tbr3sutlyi">
              Back to queue
            </Link>} data-id="element-855" data-mp="sxbo13mojhmb" />
      </div>;
  }
  const entity = entityById(record.entityId);
  const criteriaSet = criteriaSets.find(set => set.track === record.track && set.stage === record.stage) ?? criteriaSets.find(set => set.track === record.track) ?? criteriaSets[0];
  const sla = daysUntil(record.slaDueOn);
  const isMegaMajor = record.track !== 'international';
  return <div className="pb-24" data-id="element-857" data-mp="17lv6wfck1euo">
      <PageHeader eyebrow={`${trackLabel(record.track)} · ${record.ref}`} title={record.eventName} description={record.summary} actions={<div className="flex flex-wrap items-center gap-2" data-id="element-859" data-mp="ysiah3fa7v3u">
            <RecordStatusPill status={record.status} data-id="element-860" data-mp="ttot2zwvjjye" />
            <TrackPill track={record.track} data-id="element-861" data-mp="tugmq56wv7m1" />
            <ScoreChip score={record.aiScore} showBand data-id="element-862" data-mp="oum0wg1zt87u" />
            {isMegaMajor && <Link to={`/lifecycle/${record.id}`} className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-863" data-mp="osrcusvfczzj">
                <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-864" data-mp="2rg7s5dblkevn" />
                Lifecycle workspace
              </Link>}
            {record.outreachId && <Link to="/outreach" className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-865" data-mp="30hfmjkazsvkg">
                <Megaphone className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-866" data-mp="27ul4sv5k8aao" />
                Outreach campaign
              </Link>}
          </div>} data-id="element-858" data-mp="17kk4aiuuid55" />

      <div className="space-y-4 p-6" data-id="element-867" data-mp="2hr6bdkx89tgm">
        <StageStepper track={record.track} currentStage={record.stage} selectedStage={record.stage} daysInStage={record.daysInStage} onSelect={() => undefined} data-id="element-868" data-mp="2hq52nn3opj22" />

        <div className="grid gap-4 xl:grid-cols-[1.75fr_1fr]" data-id="element-869" data-mp="1p0y1egcuifd4">
          <div className="space-y-4" data-id="element-870" data-mp="1u0ap13bjjqgr">
            <Tabs defaultValue="overview" data-id="element-871" data-mp="3vr14mqmkiv15">
              <TabsList className="flex-wrap" data-id="element-872" data-mp="3vs2qzr91b3y6">
                <TabsTrigger value="overview" data-id="element-873" data-mp="3voomaccweink">Overview</TabsTrigger>
                <TabsTrigger value="entity" data-id="element-874" data-mp="3rr0sjcmxyvx9">Entity &amp; governance</TabsTrigger>
                <TabsTrigger value="event" data-id="element-875" data-mp="9chzlkjhgs7c">Event profile</TabsTrigger>
                <TabsTrigger value="budget" data-id="element-876" data-mp="4kzxuu9mynlb">Budget &amp; funding</TabsTrigger>
                <TabsTrigger value="documents" data-id="element-877" data-mp="4iwznmybxo5i">Documents</TabsTrigger>
                <TabsTrigger value="timeline" data-id="element-878" data-mp="j830tggq9og1">Timeline &amp; audit</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4" data-id="element-879" data-mp="ea9umnffcufr">
                <SectionCard title="Record summary" data-id="element-880" data-mp="s99elhrzsl5l">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-881" data-mp="nb7z16trz3yv">
                    <div data-id="element-882" data-mp="n94znw6qfyhh">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-883" data-mp="2u1p7a5vzkfdc">
                        Current stage
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-884" data-mp="2p4fc09lx19hl">
                        {STAGE_META[record.stage].label} · {record.daysInStage} days
                      </dd>
                    </div>
                    <div data-id="element-885" data-mp="19iv9b5g50h1u">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-886" data-mp="19lhj7bqi38n8">
                        Assigned officer
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-887" data-mp="zsaapi0grcix">{record.officer}</dd>
                    </div>
                    <div data-id="element-888" data-mp="lsr62z6v2jbd">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-889" data-mp="lwey1twnqxnz">
                        Service standard
                      </dt>
                      <dd className={`mt-1 text-[13px] ${sla < 0 ? 'text-uae-red' : ''}`} data-id="element-890" data-mp="cuysq0kp9lqm">
                        {sla < 0 ? `${Math.abs(sla)} days over (${formatDate(record.slaDueOn)})` : `${sla} days remaining (${formatDate(record.slaDueOn)})`}
                      </dd>
                    </div>
                    <div data-id="element-891" data-mp="3k7i8prsgskvj">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-892" data-mp="3k3krjbpg63ea">
                        Host body
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-893" data-mp="3b623rzkf5ayp">{record.hostBody}</dd>
                    </div>
                    <div data-id="element-894" data-mp="367qpncn3ceao">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-895" data-mp="368in6t090m3l">
                        Edition year
                      </dt>
                      <dd className="mt-1 font-mono text-[13px]" data-id="element-896" data-mp="2qqnstjs8zvd">{record.editionYear}</dd>
                    </div>
                    <div data-id="element-897" data-mp="3u2a9t5l99pv2">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-898" data-mp="3p51owx8q37q4">
                        Criteria set applied
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-899" data-mp="qgco5kbw5l4y">{criteriaSet.label}</dd>
                    </div>
                  </dl>
                </SectionCard>

                <GatePanel record={record} stage={record.stage} data-id="element-900" data-mp="t09aarpm6aqw" />
              </TabsContent>

              <TabsContent value="entity" className="mt-4 space-y-4" data-id="element-901" data-mp="xy127lq9dwv3">
                <SectionCard title="Applying entity" description={entityName(record.entityId)} data-id="element-902" data-mp="y0dq0fip71g2">
                  {entity ? <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-903" data-mp="16zqv6vzsh3uz">
                      <div data-id="element-904" data-mp="1bybwkyqs1tl0">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-905" data-mp="1bz3n3v5ip14f">
                          Entity type
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-906" data-mp="1lo585zqx68p9">{entity.kind}</dd>
                      </div>
                      <div data-id="element-907" data-mp="1qmykhougztnu">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-908" data-mp="1qm6m669hnhtv">
                          Emirate
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-909" data-mp="1znwgbjur8uvc">{entity.emirate}</dd>
                      </div>
                      <div data-id="element-910" data-mp="2kyl7lo0n3639">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-911" data-mp="2kxtmj74o4qr8">
                          Contact
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-912" data-mp="2uwr75yyz404y">{entity.contact}</dd>
                      </div>
                      <div data-id="element-913" data-mp="2py7m00s7esdn">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-914" data-mp="2pyzcix6y1zx2">
                          Bid record
                        </dt>
                        <dd className="mt-1 font-mono text-[13px]" data-id="element-915" data-mp="1x9inm9ij915l">
                          {entity.bidsWon} won of {entity.bidsSubmitted} submitted
                        </dd>
                      </div>
                      <div data-id="element-916" data-mp="276wtxgaqs9k6">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-917" data-mp="276ojdv23dgc5">
                          Support received to date
                        </dt>
                        <dd className="mt-1 font-mono text-[13px]" data-id="element-918" data-mp="1ehpxcmuulu1k">
                          {formatAed(entity.supportReceivedAed)}
                        </dd>
                      </div>
                      <div data-id="element-919" data-mp="3ux4fj0e528le">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-920" data-mp="3uyfiwz3o9ebz">
                          Compliance standing
                        </dt>
                        <dd className={`mt-1 text-[13px] capitalize ${entity.compliance === 'good-standing' ? 'text-uae-leaf' : entity.compliance === 'restricted' ? 'text-uae-red' : 'text-[#8a6f11]'}`} data-id="element-921" data-mp="3l69prm1ooqv7">
                          {entity.compliance.replace('-', ' ')}
                        </dd>
                      </div>
                    </dl> : <p className="text-[12px] text-muted-foreground" data-id="element-922" data-mp="2sjf9l1bwgdnj">
                      Entity record unavailable.
                    </p>}
                </SectionCard>
              </TabsContent>

              <TabsContent value="event" className="mt-4 space-y-4" data-id="element-923" data-mp="1hopyo3suwxa9">
                <SectionCard title="Event profile" data-id="element-924" data-mp="13ymi9tgnrxsq">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-925" data-mp="13vqj61t2r32p">
                    <div data-id="element-926" data-mp="17xvc0ybp7voa">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-927" data-mp="17x1zthtz0dyr">
                        Sector
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-928" data-mp="yzjcg6sutnjp">{record.sector}</dd>
                    </div>
                    <div data-id="element-929" data-mp="l0a5zyuga7sx">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-930" data-mp="l11smk62kv72">
                        Host emirate
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-931" data-mp="2fe1akjxxa1hi">{record.emirate}</dd>
                    </div>
                    <div data-id="element-932" data-mp="2jda6nvgfqbg8">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-933" data-mp="2jds8r8ps3jw2">
                        Track
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-934" data-mp="3qfrs0fog8ci">{trackLabel(record.track)}</dd>
                    </div>
                  </dl>
                  <Separator className="my-4" data-id="element-935" data-mp="hicetq2hns21" />
                  <p className="text-[13px] leading-relaxed text-muted-foreground" data-id="element-936" data-mp="cpt18t3swwx8">
                    {record.summary}
                  </p>
                </SectionCard>
              </TabsContent>

              <TabsContent value="budget" className="mt-4 space-y-4" data-id="element-937" data-mp="mgnoo0ssa2k9">
                <SectionCard title="Budget & funding" data-id="element-938" data-mp="migvph7b6oss">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-939" data-mp="10em130njnox4">
                    <div data-id="element-940" data-mp="vgao4h1ycyao">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-941" data-mp="vi3z28jlm4oh">
                        Support requested
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-942" data-mp="2x8p4hbouluoz">
                        {formatAed(record.requestedSupportAed)}
                      </dd>
                    </div>
                    <div data-id="element-943" data-mp="163lygnmmzptl">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-944" data-mp="1656win85tqw7">
                        Funding mandate
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-945" data-mp="1f4k7jkbxmnj1">
                        {record.approval ? formatAed(record.approval.mandateAed) : 'Not yet issued'}
                      </dd>
                    </div>
                    <div data-id="element-946" data-mp="1p0n8wnyymi4x">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-947" data-mp="1tt6lbhlwv785">
                        Drawn to date
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-948" data-mp="1yum2uzovqvls">
                        {record.planBuild ? formatAed(record.planBuild.budgetDrawnAed) : 'Nil'}
                      </dd>
                    </div>
                  </dl>
                  {record.approval && record.approval.conditions.length > 0 && <>
                      <Separator className="my-4" data-id="element-949" data-mp="3jatyqg0lx6mg" />
                      <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-950" data-mp="3jbmyuy528xvm">
                        Conditions attached to funding
                      </p>
                      <ul className="mt-2 space-y-2" data-id="element-951" data-mp="ewvir3gdu4i4">
                        {record.approval.conditions.map(condition => <li key={condition} className="rounded-sm border border-border bg-muted/50 px-3 py-2 text-[12px]" data-id="element-952" data-mp="avk0xk3r2nkh">
                            {condition}
                          </li>)}
                      </ul>
                    </>}
                </SectionCard>
              </TabsContent>

              <TabsContent value="documents" className="mt-4" data-id="element-953" data-mp="ts1nzo7nlzae">
                <SectionCard title="Documents" description="Verification state of every artefact attached to the record" bodyClassName="p-0" data-id="element-954" data-mp="dctxa3u02ut2">
                  <ul className="divide-y divide-border" data-id="element-955" data-mp="4fjk2cxmgvli">
                    {record.documents.map(document => <li key={document.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-956" data-mp="9empvzlff5uj">
                        <div className="min-w-0" data-id="element-957" data-mp="1136gwbylyfzh">
                          <p className="truncate text-[13px]" data-id="element-958" data-mp="ndcojjnw9dl0">{document.label}</p>
                          <p className="text-[11px] text-muted-foreground" data-id="element-959" data-mp="sbe8s7x23iy2">
                            Updated {formatDate(document.updatedOn)}
                          </p>
                        </div>
                        <DocumentStatusBadge status={document.status} data-id="element-960" data-mp="1krozgap3yv57" />
                      </li>)}
                  </ul>
                </SectionCard>
              </TabsContent>

              <TabsContent value="timeline" className="mt-4" data-id="element-961" data-mp="1q5f8rnife3cq">
                <SectionCard title="Timeline & audit" description="Every decision, advancement and override attributed to its author" data-id="element-962" data-mp="1q32rzdoelz19">
                  <ol className="relative space-y-4 border-l border-border pl-5" data-id="element-963" data-mp="1yy8zch3bop5w">
                    {[...record.timeline].reverse().map(event => <li key={event.id} className="relative" data-id="element-964" data-mp="176t7a7izya8a">
                        <span className="absolute -left-[23px] top-1 h-2 w-2 rounded-full bg-uae-green" aria-hidden="true" data-id="element-965" data-mp="178nphe2vvo8o" />
                        <p className="text-[13px] font-medium" data-id="element-966" data-mp="1g9c3i8sa1olk">{event.label}</p>
                        <p className="text-[11px] text-muted-foreground" data-id="element-967" data-mp="2wjeicskzicma">
                          {formatDate(event.on)} · {event.actor}
                        </p>
                        {event.detail && <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground" data-id="element-968" data-mp="2zox4a9usys0n">
                            {event.detail}
                          </p>}
                      </li>)}
                  </ol>
                </SectionCard>
              </TabsContent>
            </Tabs>
          </div>

          <AiInsightRail record={record} bands={criteriaSet.bands} criteriaSetLabel={criteriaSet.label} data-id="element-969" data-mp="2fzwbubopw04j" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-card/95 px-6 py-3 backdrop-blur lg:left-60" data-id="element-970" data-mp="2pwixv3ss4x85">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-971" data-mp="2uosroux0mhfp">
          <p className="text-[12px] text-muted-foreground" data-id="element-972" data-mp="1x8vh5zumqqqs">
            Decisions are recorded against{' '}
            <span className="font-mono" data-id="element-973" data-mp="226ok8k0mz4mu">{record.ref}</span> with author and rationale.
          </p>
          <div className="flex flex-wrap items-center gap-2" data-id="element-974" data-mp="1uo78s8979fr8">
            <Button variant="outline" size="sm" onClick={() => setDecision('request-info')} data-id="element-975" data-mp="1poumbom8qap6">
              Request information
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDecision('escalate')} data-id="element-976" data-mp="1koq0dj2kcnrk">
              Escalate to committee
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDecision('reject')} data-id="element-977" data-mp="1ft0yh932mus3">
              Decline
            </Button>
            <Button size="sm" onClick={() => setDecision('approve')} data-id="element-978" data-mp="16y34dyo6tbz6">
              Approve at stage
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-979" data-mp="11ygt3urx65t0" />
            </Button>
          </div>
        </div>
      </div>

      <DecisionDialog record={record} kind={decision} onClose={() => setDecision(null)} data-id="element-980" data-mp="x204kzgt5pfe" />
    </div>;
}